import { Spinner } from '@/components';
import { ConfirmDialog } from '@/components/Account/ConfirmDialog';
import { MasterTranslation } from '@/constants';
import { deleteAccount } from '@/fetchers/umsAPI/user.fetchers';
import { useGaEventBaseParams, useGlobalState } from '@/hooks';
import { ButtonSize } from '@/interfaces';
import { renderHTML } from '@/utils';
import { type FC, useCallback, useState } from 'react';
import { BackButton, BackButtonText, StyledArrowBackIcon } from '../../DataPrivacy.styles';
import DeleteSuccess from '../DeleteSuccess';
import {
  ConsequencesList,
  DeleteAccountContent,
  DeleteAccountWrapper,
  DeleteButtonWrapper,
  Description,
  ErrorText,
  Text,
  Title,
  WarningBox,
  WarningIcon,
} from './DeleteAccount.styles';

interface DeleteAccountProps {
  onBackClick: () => void;
}

const DeleteAccount: FC<DeleteAccountProps> = ({ onBackClick }) => {
  const { t, userInfo, clearUserInfo } = useGlobalState();
  const getGaEventBaseParams = useGaEventBaseParams();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const onDelete = useCallback(async () => {
    setIsDeleting(true);
    setIsError(false);
    setShowDialog(false);

    try {
      const gaEventBaseParams = getGaEventBaseParams();
      const result = await deleteAccount(gaEventBaseParams);

      if (result) {
        setIsDeleted(true);
        clearUserInfo();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setIsError(true);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setIsError(true);
    } finally {
      setIsDeleting(false);
    }
  }, [clearUserInfo]);

  if (isDeleted) return <DeleteSuccess />;

  if (!userInfo?.email) return null;

  const warningTextHTML = renderHTML(
    t(MasterTranslation.AccDeleteAccountSubtitle).replace('{email}', `<b>${userInfo.email}</b>`),
    Text,
  );

  return (
    <DeleteAccountWrapper>
      <DeleteAccountContent>
        <BackButton onClick={onBackClick} disabled={isDeleting}>
          <StyledArrowBackIcon title='Back arrow' />
          <BackButtonText>{t(MasterTranslation.AccBackToPreviousPage)}</BackButtonText>
        </BackButton>

        <Title>{t(MasterTranslation.AccDeleteAccountTitle)}</Title>

        <WarningBox>
          <WarningIcon />
          {warningTextHTML}
        </WarningBox>

        <Description>
          <Text>{t(MasterTranslation.AccDeleteAccountListStart)}</Text>
          <ConsequencesList>
            <li>{t(MasterTranslation.AccDeleteAccountListItem1)}</li>
            <li>{t(MasterTranslation.AccDeleteAccountListItem2)}</li>
            <li>{t(MasterTranslation.AccDeleteAccountListItem3)}</li>
          </ConsequencesList>
          <Text>{t(MasterTranslation.AccDeleteAccountListEnd)}</Text>
        </Description>

        <DeleteButtonWrapper
          disabled={isDeleting}
          isPMTButton
          truncate={2}
          size={ButtonSize.Large}
          onClick={() => setShowDialog(true)}
        >
          {isDeleting ? (
            <Spinner text={t(MasterTranslation.AccDeleteAccountCtaUpperCase)} />
          ) : (
            t(MasterTranslation.AccDeleteAccountCtaUpperCase)
          )}
        </DeleteButtonWrapper>

        <ConfirmDialog
          showDialog={showDialog}
          setShowDialog={setShowDialog}
          onConfirm={onDelete}
          mainText={t(MasterTranslation.AccDeleteAccountModalTitle)}
          subText={t(MasterTranslation.AccDeleteAccountModalSubtitle)}
          cancelText={t(MasterTranslation.AccDeleteAccountCancelCta)}
          confirmText={t(MasterTranslation.AccDeleteAccountConfirmationCta)}
        />

        {isError && <ErrorText>{t(MasterTranslation.AccDeleteAccountError)}</ErrorText>}
      </DeleteAccountContent>
    </DeleteAccountWrapper>
  );
};

export default DeleteAccount;
